import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEligibleTranslators, assignTranslators } from '../projectSlice';
import { Modal, List, Checkbox, message, Spin, Select, Button, Divider } from 'antd';

const AssignTranslatorsModal = ({ project, visible, onClose }) => {
  const dispatch = useDispatch();
  const { eligibleTranslators } = useSelector((state) => state.projects);
  const [selectedTranslators, setSelectedTranslators] = useState([]);
  // Now assignments is a flat array of objects; multiple assignments per translator are allowed
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);

  // When modal opens, fetch eligible translators and prepopulate existing assignments
  useEffect(() => {
    if (visible && project?.id) {
      setLoading(true);
      dispatch(fetchEligibleTranslators(project.id))
        .unwrap()
        .then((res) => {
          // Prepopulate assignments if there are any saved for this project.
          if (res.data.assignments) {
            setAssignments(
              res.data.assignments.map(a => ({
                translator_id: a.translator.id,
                service_id: a.service.id,
                // Use the property name as returned by the API: "target_languages"
                target_language_ids: a.target_languages.map(lang => lang.id),
              }))
            );
          }
        })
        .finally(() => setLoading(false));
    }
  }, [visible, project?.id, dispatch]);

  // Handle selecting/deselecting translators
  const handleTranslatorSelection = (translatorId, checked) => {
    if (checked) {
      setSelectedTranslators(prev => [...prev, translatorId]);
    } else {
      setSelectedTranslators(prev => prev.filter(id => id !== translatorId));
      // Remove all assignments for the deselected translator
      setAssignments(prev => prev.filter(a => a.translator_id !== translatorId));
    }
  };

  // Add a new (empty) assignment for a translator
  const addAssignmentForTranslator = translatorId => {
    setAssignments(prev => [
      ...prev,
      { translator_id: translatorId, service_id: null, target_language_ids: [] }
    ]);
  };

  // Update an assignment field (service or target languages)
  const updateAssignment = (assignmentIndex, field, value) => {
    const newAssignments = [...assignments];
    newAssignments[assignmentIndex][field] = value;
    setAssignments(newAssignments);
  };

  // Remove an assignment from the list
  const removeAssignment = (assignmentIndex) => {
    setAssignments(prev => prev.filter((_, index) => index !== assignmentIndex));
  };

  // Submit assignments
  const handleAssign = async () => {
    if (assignments.length === 0) {
      message.error('Please add at least one assignment.');
      return;
    }
    // Optionally, add additional validations (e.g. check for null service_id)
    try {
      await dispatch(assignTranslators({ projectId: project.id, assignments })).unwrap();
      message.success('Translators assigned successfully');
      onClose();
    } catch (error) {
      message.error(error.message || 'Failed to assign translators');
    }
  };

  // Make sure we have the required data from the backend
  const projectData = eligibleTranslators[project.id] || {};
  const {
    eligible_translators = [],
    project_services = [],
    project_target_languages = []
  } = projectData;

  return (
    <Modal
      visible={visible}
      title="Assign Translators"
      onCancel={onClose}
      onOk={handleAssign}
      okText="Assign"
      width={800}
    >
      {loading ? (
        <Spin />
      ) : (
        <div>
          <h3>Select Translators</h3>
          <List
            bordered
            dataSource={eligible_translators}
            locale={{ emptyText: 'No eligible translators found.' }}
            renderItem={translator => (
              <List.Item>
                <Checkbox
                  onChange={e => handleTranslatorSelection(translator.id, e.target.checked)}
                  checked={selectedTranslators.includes(translator.id)}
                >
                  {translator.name}
                </Checkbox>
              </List.Item>
            )}
          />

          <h3>Assign Services and Target Languages</h3>
          {selectedTranslators.map(translatorId => {
            const translator = eligible_translators.find(t => t.id === translatorId);
            // Filter assignments belonging to this translator
            const translatorAssignments = assignments.filter(a => a.translator_id === translatorId);
            return (
              <div key={translatorId} style={{ marginBottom: 16, border: '1px solid #f0f0f0', padding: 8 }}>
                <h4>{translator?.name}</h4>
                {translatorAssignments.map((assignment, index) => {
                  // Get the overall index of this assignment in the assignments array
                  const globalIndex = assignments.findIndex((a, i) =>
                    a.translator_id === translatorId &&
                    a.service_id === assignment.service_id &&
                    a.target_language_ids.join(',') === assignment.target_language_ids.join(',')
                  );
                  return (
                    <div key={globalIndex} style={{ marginBottom: 8 }}>
                      <div>
                        <span>Service:</span>
                        <Select
                          placeholder="Select a service"
                          style={{ width: '100%', marginBottom: 8 }}
                          value={assignment.service_id}
                          onChange={serviceId =>
                            updateAssignment(globalIndex, 'service_id', serviceId)
                          }
                        >
                          {project_services.map(service => (
                            <Select.Option key={service.id} value={service.id}>
                              {service.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <span>Target Languages:</span>
                        <Select
                          mode="multiple"
                          placeholder="Select target languages"
                          style={{ width: '100%' }}
                          value={assignment.target_language_ids}
                          onChange={targetLanguageIds =>
                            updateAssignment(globalIndex, 'target_language_ids', targetLanguageIds)
                          }
                        >
                          {project_target_languages.map(lang => (
                            <Select.Option key={lang.id} value={lang.id}>
                              {lang.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <Button
                        type="link"
                        danger
                        onClick={() => removeAssignment(globalIndex)}
                      >
                        Remove Assignment
                      </Button>
                      <Divider />
                    </div>
                  );
                })}
                <Button type="dashed" onClick={() => addAssignmentForTranslator(translatorId)}>
                  Add Assignment for {translator.name}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default AssignTranslatorsModal;
